import { useState, useEffect } from 'react';

export default function Sidebar() {
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);

  const tableOfContents = [
    { id: '01', label: 'Portfolio', href: '#portfolio', icon: 'ri-book-2-line' },
    { id: '02', label: 'About Me', href: '#about', icon: 'ri-user-line' },
    { id: '03', label: 'Resume', href: 'https://docs.google.com/document/d/10d3nDv9ymrFB3AJMUW-1p-GjddIVsGkEBsFARIfy5EY/edit?usp=sharing', icon: 'ri-file-text-line', external: true },
    { id: '04', label: 'Contact Me', href: '#contact', icon: 'ri-pencil-line' },
  ];

  const nodes = [
    { id: '05', label: 'Email', href: 'mailto:annieding000@gmail.com', external: true, icon: 'ri-mail-line' },
    { id: '06', label: 'LinkedIn', href: 'https://www.linkedin.com/in/annieding04/', external: true, icon: 'ri-linkedin-line' },
    { id: '07', label: 'Instagram', href: 'https://www.instagram.com/ani_fatal/', external: true, icon: 'ri-instagram-line' },
  ];

  return (
    <aside className="lg:w-[240px] lg:min-w-[240px] lg:sticky lg:top-8 lg:self-start">
      <div className="bg-notion-sidebar rounded-lg p-3 border border-notion-border">
        {/* Name / Logo */}
        <div className="mb-3">
          <a href="/" className="inline-block cursor-pointer group">
            <h1 className="text-lg font-normal tracking-tight text-notion-text" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
              Annie Ding
              <span className="inline-block ml-1 text-[#9333EA] group-hover:rotate-12 transition-transform duration-300">✦</span>
            </h1>
          </a>
        </div>

        {/* Bio */}
        <div className="mb-3 text-[12px] leading-[1.6] text-notion-secondary">
          <p className="mb-1.5">
            Originally from Los Angeles, I&apos;m passionate about blending business, creativity, and design to create innovative and impactful products.
          </p>
          <p>
            4th Year, Econ &amp; Design{' '}
            <a
              href="https://www.ucdavis.edu/"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="text-notion-text underline decoration-notion-border hover:decoration-notion-text transition-colors cursor-pointer"
            >
              @UC Davis
            </a>
          </p>
        </div>

        {/* Divider */}
        <div className="h-px bg-notion-border mb-3"></div>

        {/* Table of Contents */}
        <div className="mb-3">
          <h3 className="text-[10px] font-medium text-notion-secondary mb-1.5 uppercase tracking-widest">
            On this page
          </h3>
          <div className="space-y-0.5">
            {tableOfContents.map((item) => (
              <a
                key={item.id}
                href={item.href}
                target={item.external ? '_blank' : undefined}
                rel={item.external ? 'noopener noreferrer nofollow' : undefined}
                className="flex items-center gap-2 text-[12px] text-notion-text hover:bg-notion-hover rounded-md px-2 py-1 transition-colors cursor-pointer group"
                onMouseEnter={() => setHoveredItem(item.id)}
                onMouseLeave={() => setHoveredItem(null)}
              >
                <span className="w-3.5 h-3.5 flex items-center justify-center text-notion-secondary">
                  <i className={`${item.icon} text-xs`}></i>
                </span>
                <span>{item.label}</span>
                <span
                  className={`ml-auto text-notion-secondary text-xs transition-opacity ${
                    hoveredItem === item.id ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  {item.external ? '↗' : '→'}
                </span>
              </a>
            ))}
          </div>
        </div>

        {/* Divider */}
        <div className="h-px bg-notion-border mb-3"></div>

        {/* Links */}
        <div className="mb-3">
          <h3 className="text-[10px] font-medium text-notion-secondary mb-1.5 uppercase tracking-widest">
            Connect
          </h3>
          <div className="space-y-0.5">
            {nodes.map((item) => (
              <a
                key={item.id}
                href={item.href}
                target={item.external ? '_blank' : undefined}
                rel={item.external ? 'noopener noreferrer nofollow' : undefined}
                className="flex items-center gap-2 text-[12px] text-notion-text hover:bg-notion-hover rounded-md px-2 py-1 transition-colors cursor-pointer group"
                onMouseEnter={() => setHoveredItem(item.id)}
                onMouseLeave={() => setHoveredItem(null)}
              >
                <span className="w-3.5 h-3.5 flex items-center justify-center text-notion-secondary">
                  <i className={`${item.icon} text-xs`}></i>
                </span>
                <span>{item.label}</span>
              </a>
            ))}
          </div>
        </div>

        {/* Copyright */}
        <div className="text-[10px] text-notion-secondary/60 pt-0.5">
          &copy;2025 Annie Ding
        </div>
      </div>

      {/* Play Area / Gaming Box - Desktop Only */}
      <div className="mt-2 bg-notion-sidebar rounded-lg border border-notion-border overflow-hidden hidden lg:block">
        <div className="w-full h-[100px] overflow-hidden relative bg-[#f5f0e8]">
          <PixelScene />
        </div>
        <div className="px-3 py-2">
          <div className="flex items-center gap-1.5 mb-0.5">
            <span className="w-3.5 h-3.5 flex items-center justify-center text-notion-accent">
              <i className="ri-gamepad-line text-xs"></i>
            </span>
            <span className="text-[10px] font-medium text-notion-secondary uppercase tracking-widest">Play Area</span>
          </div>
          <p className="text-[11px] leading-[1.5] text-notion-secondary">
            When I&apos;m offline, I&apos;m usually immersed in a game, saving the world one quest at a time.
          </p>
        </div>
      </div>
    </aside>
  );
}

export function PlayAreaMobile() {
  return (
    <div className="bg-notion-sidebar rounded-lg border border-notion-border overflow-hidden lg:hidden">
      <div className="w-full h-[100px] overflow-hidden relative bg-[#f5f0e8]">
        <PixelSceneMobile />
      </div>
      <div className="px-3 py-2">
        <div className="flex items-center gap-1.5 mb-0.5">
          <span className="w-3.5 h-3.5 flex items-center justify-center text-notion-accent">
            <i className="ri-gamepad-line text-xs"></i>
          </span>
          <span className="text-[10px] font-medium text-notion-secondary uppercase tracking-widest">Play Area</span>
        </div>
        <p className="text-[11px] leading-[1.5] text-notion-secondary">
          When I&apos;m offline, I&apos;m usually immersed in a game, saving the world one quest at a time.
        </p>
      </div>
    </div>
  );
}

function PixelSceneMobile() {
  const [frame, setFrame] = useState(0);
  const [swordSwing, setSwordSwing] = useState(false);
  const [slimes, setSlimes] = useState([
    { id: 1, x: 180, alive: true, hit: false, hp: 2 },
    { id: 2, x: 215, alive: true, hit: false, hp: 2 },
    { id: 3, x: 250, alive: true, hit: false, hp: 2 },
  ]);
  const [playerX, setPlayerX] = useState(120);
  const [walkDir, setWalkDir] = useState(1);

  useEffect(() => {
    const interval = setInterval(() => {
      setFrame((f) => f + 1);
    }, 150);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const attackInterval = setInterval(() => {
      setSwordSwing(true);
      setTimeout(() => {
        setSlimes((prev) =>
          prev.map((s) => {
            if (s.alive && Math.abs(playerX + 20 - s.x) < 40) {
              const newHp = s.hp - 1;
              return { ...s, hit: true, hp: newHp, alive: newHp > 0 };
            }
            return s;
          })
        );
        setTimeout(() => {
          setSlimes((prev) => prev.map((s) => ({ ...s, hit: false })));
        }, 200);
        setSwordSwing(false);
      }, 200);
    }, 1200);
    return () => clearInterval(attackInterval);
  }, [playerX]);

  useEffect(() => {
    const allDead = slimes.every((s) => !s.alive);
    if (allDead) {
      setTimeout(() => {
        setSlimes([
          { id: Date.now(), x: 180 + Math.random() * 20, alive: true, hit: false, hp: 2 },
          { id: Date.now() + 1, x: 215 + Math.random() * 20, alive: true, hit: false, hp: 2 },
          { id: Date.now() + 2, x: 250 + Math.random() * 20, alive: true, hit: false, hp: 2 },
        ]);
      }, 800);
    }
  }, [slimes]);

  useEffect(() => {
    const moveInterval = setInterval(() => {
      setPlayerX((prev) => {
        const next = prev + walkDir * 2;
        if (next > 160) { setWalkDir(-1); return prev - 2; }
        if (next < 80) { setWalkDir(1); return prev + 2; }
        return next;
      });
    }, 200);
    return () => clearInterval(moveInterval);
  }, [walkDir]);

  const bounce = frame % 4 < 2 ? 0 : -2;
  const slimeBounce = frame % 6 < 3 ? 0 : 1;

  return (
    <svg viewBox="0 0 280 140" className="w-full h-full" style={{ imageRendering: 'pixelated' }}>
      {/* Ground */}
      <rect x="0" y="110" width="280" height="30" fill="#c8b88a" />
      <rect x="0" y="108" width="280" height="4" fill="#a89868" />
      {/* Grass tufts */}
      {[20, 55, 90, 130, 170, 210, 245].map((gx, i) => (
        <g key={i}>
          <rect x={gx} y="104" width="2" height="6" fill="#7a9e5a" />
          <rect x={gx + 4} y="102" width="2" height="8" fill="#8ab66a" />
          <rect x={gx + 8} y="105" width="2" height="5" fill="#7a9e5a" />
        </g>
      ))}
      {/* Clouds */}
      <g opacity="0.4">
        <rect x={40 + (frame % 80) * 0.3} y="15" width="24" height="6" rx="3" fill="#d4cfc4" />
        <rect x={36 + (frame % 80) * 0.3} y="19" width="32" height="6" rx="3" fill="#d4cfc4" />
        <rect x={160 + (frame % 60) * 0.2} y="22" width="20" height="5" rx="2" fill="#d4cfc4" />
        <rect x={156 + (frame % 60) * 0.2} y="25" width="28" height="5" rx="2" fill="#d4cfc4" />
      </g>

      {/* Player Character */}
      <g transform={`translate(${playerX}, ${66 + bounce})`}>
        <rect x="3" y="0" width="14" height="3" fill="#2a1810" />
        <rect x="2" y="2" width="16" height="4" fill="#3a2820" />
        <rect x="4" y="4" width="12" height="2" fill="#2a1810" />
        <rect x="4" y="5" width="12" height="1" fill="#c94535" />
        <rect x="2" y="6" width="16" height="2" fill="#2a1810" />
        <rect x="1" y="8" width="18" height="8" fill="#2a1810" />
        <rect x="0" y="14" width="4" height="12" fill="#2a1810" />
        <rect x="16" y="14" width="4" height="12" fill="#2a1810" />
        <rect x="5" y="8" width="10" height="8" fill="#f5c5a3" />
        <rect x="7" y="11" width="2" height="2" fill="#2a2a2a" />
        <rect x="11" y="11" width="2" height="2" fill="#2a2a2a" />
        <rect x="7" y="12" width="2" height="1" fill="#4a3a2a" />
        <rect x="11" y="12" width="2" height="1" fill="#4a3a2a" />
        <rect x="5" y="16" width="10" height="3" fill="#ffffff" />
        <rect x="4" y="17" width="12" height="1" fill="#f0f0f0" />
        <rect x="4" y="19" width="12" height="8" fill="#e85d4a" />
        <rect x="6" y="20" width="2" height="2" fill="#4a3a2a" />
        <rect x="12" y="20" width="2" height="2" fill="#4a3a2a" />
        <rect x="9" y="22" width="2" height="2" fill="#4a3a2a" />
        <rect x="6" y="24" width="2" height="2" fill="#4a3a2a" />
        <rect x="12" y="24" width="2" height="2" fill="#4a3a2a" />
        <rect x="7" y="21" width="1" height="1" fill="#2a1810" />
        <rect x="11" y="21" width="1" height="1" fill="#2a1810" />
        <rect x="8" y="23" width="1" height="1" fill="#2a1810" />
        <rect x="10" y="23" width="1" height="1" fill="#2a1810" />
        <rect x="4" y="27" width="12" height="6" fill="#2a2a3a" />
        <rect x="6" y="27" width="1" height="6" fill="#1a1a2a" />
        <rect x="9" y="27" width="1" height="6" fill="#1a1a2a" />
        <rect x="12" y="27" width="1" height="6" fill="#1a1a2a" />
        <rect x="5" y="33" width="4" height="4" fill="#f5c5a3" />
        <rect x="11" y="33" width="4" height="4" fill="#f5c5a3" />
        <rect x="5" y="37" width="4" height="4" fill="#8b2e2e" />
        <rect x="11" y="37" width="4" height="4" fill="#8b2e2e" />
        <rect x="5" y="37" width="4" height="1" fill="#a84444" />
        <rect x="11" y="37" width="4" height="1" fill="#a84444" />
        <rect x="4" y="41" width="5" height="4" fill="#1a1a1a" />
        <rect x="11" y="41" width="5" height="4" fill="#1a1a1a" />
        <rect x="4" y="43" width="5" height="1" fill="#2a2a2a" />
        <rect x="11" y="43" width="5" height="1" fill="#2a2a2a" />
        <g transform={swordSwing ? 'rotate(-60, 18, 22)' : 'rotate(-20, 18, 22)'} style={{ transition: 'transform 0.15s' }}>
          <rect x="18" y="16" width="2" height="5" fill="#8b6914" />
          <rect x="18" y="17" width="2" height="1" fill="#a88020" />
          <rect x="18" y="19" width="2" height="1" fill="#a88020" />
          <rect x="16" y="14" width="6" height="2" fill="#c0a060" />
          <rect x="15" y="14" width="8" height="1" fill="#d4b870" />
          <rect x="18" y="4" width="2" height="12" fill="#d0d0d0" />
          <rect x="19" y="5" width="1" height="8" fill="#f0f0f0" />
          <rect x="18" y="2" width="2" height="3" fill="#e0e0e0" />
          <rect x="19" y="1" width="1" height="2" fill="#f5f5f5" />
        </g>
      </g>

      {/* Slimes */}
      {slimes.map((slime) =>
        slime.alive ? (
          <g
            key={slime.id}
            transform={`translate(${slime.x}, ${92 + slimeBounce})`}
            opacity={slime.hit ? 0.5 : 1}
            style={{ transition: 'opacity 0.1s' }}
          >
            <rect x="2" y="4" width="16" height="10" rx="2" fill="#6ec44e" />
            <rect x="0" y="8" width="20" height="8" rx="2" fill="#5ab43e" />
            <rect x="4" y="6" width="3" height="3" fill="#2a2a2a" />
            <rect x="12" y="6" width="3" height="3" fill="#2a2a2a" />
            <rect x="5" y="7" width="1" height="1" fill="#ffffff" />
            <rect x="13" y="7" width="1" height="1" fill="#ffffff" />
          </g>
        ) : (
          <g key={slime.id} transform={`translate(${slime.x}, ${104})`} opacity="0.3">
            <rect x="2" y="0" width="16" height="4" rx="1" fill="#5ab43e" />
            <rect x="8" y="-8" width="2" height="2" fill="#ffd700" opacity={(frame % 4 < 2) ? 1 : 0} />
            <rect x="14" y="-4" width="2" height="2" fill="#ffd700" opacity={(frame % 4 >= 2) ? 1 : 0} />
          </g>
        )
      )}

      {/* Floating damage numbers */}
      {slimes.map((slime) =>
        slime.hit && slime.alive ? (
          <text
            key={`dmg-${slime.id}`}
            x={slime.x + 8}
            y={82}
            textAnchor="middle"
            fill="#e85d4a"
            fontSize="8"
            fontFamily="monospace"
            fontWeight="bold"
          >
            -1
          </text>
        ) : null
      )}
    </svg>
  );
}

function PixelScene() {
  const [frame, setFrame] = useState(0);
  const [swordSwing, setSwordSwing] = useState(false);
  const [slimes, setSlimes] = useState([
    { id: 1, x: 180, alive: true, hit: false, hp: 2 },
    { id: 2, x: 215, alive: true, hit: false, hp: 2 },
    { id: 3, x: 250, alive: true, hit: false, hp: 2 },
  ]);
  const [playerX, setPlayerX] = useState(120);
  const [walkDir, setWalkDir] = useState(1);

  useEffect(() => {
    const interval = setInterval(() => {
      setFrame((f) => f + 1);
    }, 150);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const attackInterval = setInterval(() => {
      setSwordSwing(true);
      setTimeout(() => {
        setSlimes((prev) =>
          prev.map((s) => {
            if (s.alive && Math.abs(playerX + 20 - s.x) < 40) {
              const newHp = s.hp - 1;
              return { ...s, hit: true, hp: newHp, alive: newHp > 0 };
            }
            return s;
          })
        );
        setTimeout(() => {
          setSlimes((prev) => prev.map((s) => ({ ...s, hit: false })));
        }, 200);
        setSwordSwing(false);
      }, 200);
    }, 1200);
    return () => clearInterval(attackInterval);
  }, [playerX]);

  useEffect(() => {
    const allDead = slimes.every((s) => !s.alive);
    if (allDead) {
      setTimeout(() => {
        setSlimes([
          { id: Date.now(), x: 180 + Math.random() * 20, alive: true, hit: false, hp: 2 },
          { id: Date.now() + 1, x: 215 + Math.random() * 20, alive: true, hit: false, hp: 2 },
          { id: Date.now() + 2, x: 250 + Math.random() * 20, alive: true, hit: false, hp: 2 },
        ]);
      }, 800);
    }
  }, [slimes]);

  useEffect(() => {
    const moveInterval = setInterval(() => {
      setPlayerX((prev) => {
        const next = prev + walkDir * 2;
        if (next > 160) { setWalkDir(-1); return prev - 2; }
        if (next < 80) { setWalkDir(1); return prev + 2; }
        return next;
      });
    }, 200);
    return () => clearInterval(moveInterval);
  }, [walkDir]);

  const bounce = frame % 4 < 2 ? 0 : -2;
  const slimeBounce = frame % 6 < 3 ? 0 : 1;

  return (
    <svg viewBox="0 0 280 140" className="w-full h-full" style={{ imageRendering: 'pixelated' }}>
      {/* Ground */}
      <rect x="0" y="110" width="280" height="30" fill="#c8b88a" />
      <rect x="0" y="108" width="280" height="4" fill="#a89868" />
      {/* Grass tufts */}
      {[20, 55, 90, 130, 170, 210, 245].map((gx, i) => (
        <g key={i}>
          <rect x={gx} y="104" width="2" height="6" fill="#7a9e5a" />
          <rect x={gx + 4} y="102" width="2" height="8" fill="#8ab66a" />
          <rect x={gx + 8} y="105" width="2" height="5" fill="#7a9e5a" />
        </g>
      ))}
      {/* Clouds */}
      <g opacity="0.4">
        <rect x={40 + (frame % 80) * 0.3} y="15" width="24" height="6" rx="3" fill="#d4cfc4" />
        <rect x={36 + (frame % 80) * 0.3} y="19" width="32" height="6" rx="3" fill="#d4cfc4" />
        <rect x={160 + (frame % 60) * 0.2} y="22" width="20" height="5" rx="2" fill="#d4cfc4" />
        <rect x={156 + (frame % 60) * 0.2} y="25" width="28" height="5" rx="2" fill="#d4cfc4" />
      </g>

      {/* Player Character - Stylish Girl with Bowler Hat */}
      <g transform={`translate(${playerX}, ${66 + bounce})`}>
        {/* Bowler Hat */}
        <rect x="3" y="0" width="14" height="3" fill="#2a1810" />
        <rect x="2" y="2" width="16" height="4" fill="#3a2820" />
        <rect x="4" y="4" width="12" height="2" fill="#2a1810" />
        {/* Hat band with red accent */}
        <rect x="4" y="5" width="12" height="1" fill="#c94535" />
        
        {/* Long Hair */}
        <rect x="2" y="6" width="16" height="2" fill="#2a1810" />
        <rect x="1" y="8" width="18" height="8" fill="#2a1810" />
        <rect x="0" y="14" width="4" height="12" fill="#2a1810" />
        <rect x="16" y="14" width="4" height="12" fill="#2a1810" />
        
        {/* Head/Face */}
        <rect x="5" y="8" width="10" height="8" fill="#f5c5a3" />
        
        {/* Eyes - stylish */}
        <rect x="7" y="11" width="2" height="2" fill="#2a2a2a" />
        <rect x="11" y="11" width="2" height="2" fill="#2a2a2a" />
        <rect x="7" y="12" width="2" height="1" fill="#4a3a2a" />
        <rect x="11" y="12" width="2" height="1" fill="#4a3a2a" />
        
        {/* White Collar/Shirt */}
        <rect x="5" y="16" width="10" height="3" fill="#ffffff" />
        <rect x="4" y="17" width="12" height="1" fill="#f0f0f0" />
        
        {/* Orange Argyle Sweater */}
        <rect x="4" y="19" width="12" height="8" fill="#e85d4a" />
        {/* Argyle pattern - dark diamonds */}
        <rect x="6" y="20" width="2" height="2" fill="#4a3a2a" />
        <rect x="12" y="20" width="2" height="2" fill="#4a3a2a" />
        <rect x="9" y="22" width="2" height="2" fill="#4a3a2a" />
        <rect x="6" y="24" width="2" height="2" fill="#4a3a2a" />
        <rect x="12" y="24" width="2" height="2" fill="#4a3a2a" />
        {/* Argyle diagonal lines */}
        <rect x="7" y="21" width="1" height="1" fill="#2a1810" />
        <rect x="11" y="21" width="1" height="1" fill="#2a1810" />
        <rect x="8" y="23" width="1" height="1" fill="#2a1810" />
        <rect x="10" y="23" width="1" height="1" fill="#2a1810" />
        
        {/* Pleated Skirt */}
        <rect x="4" y="27" width="12" height="6" fill="#2a2a3a" />
        <rect x="6" y="27" width="1" height="6" fill="#1a1a2a" />
        <rect x="9" y="27" width="1" height="6" fill="#1a1a2a" />
        <rect x="12" y="27" width="1" height="6" fill="#1a1a2a" />
        
        {/* Legs with knee-high socks */}
        <rect x="5" y="33" width="4" height="4" fill="#f5c5a3" />
        <rect x="11" y="33" width="4" height="4" fill="#f5c5a3" />
        {/* Red/burgundy socks */}
        <rect x="5" y="37" width="4" height="4" fill="#8b2e2e" />
        <rect x="11" y="37" width="4" height="4" fill="#8b2e2e" />
        {/* Sock tops */}
        <rect x="5" y="37" width="4" height="1" fill="#a84444" />
        <rect x="11" y="37" width="4" height="1" fill="#a84444" />
        
        {/* Black boots */}
        <rect x="4" y="41" width="5" height="4" fill="#1a1a1a" />
        <rect x="11" y="41" width="5" height="4" fill="#1a1a1a" />
        <rect x="4" y="43" width="5" height="1" fill="#2a2a2a" />
        <rect x="11" y="43" width="5" height="1" fill="#2a2a2a" />
        
        {/* Stylish Sword with ornate handle */}
        <g transform={swordSwing ? 'rotate(-60, 18, 22)' : 'rotate(-20, 18, 22)'} style={{ transition: 'transform 0.15s' }}>
          {/* Handle wrap */}
          <rect x="18" y="16" width="2" height="5" fill="#8b6914" />
          <rect x="18" y="17" width="2" height="1" fill="#a88020" />
          <rect x="18" y="19" width="2" height="1" fill="#a88020" />
          {/* Guard */}
          <rect x="16" y="14" width="6" height="2" fill="#c0a060" />
          <rect x="15" y="14" width="8" height="1" fill="#d4b870" />
          {/* Blade with shine */}
          <rect x="18" y="4" width="2" height="12" fill="#d0d0d0" />
          <rect x="19" y="5" width="1" height="8" fill="#f0f0f0" />
          {/* Tip */}
          <rect x="18" y="2" width="2" height="3" fill="#e0e0e0" />
          <rect x="19" y="1" width="1" height="2" fill="#f5f5f5" />
        </g>
      </g>

      {/* Slimes */}
      {slimes.map((slime) =>
        slime.alive ? (
          <g
            key={slime.id}
            transform={`translate(${slime.x}, ${92 + slimeBounce})`}
            opacity={slime.hit ? 0.5 : 1}
            style={{ transition: 'opacity 0.1s' }}
          >
            <rect x="2" y="4" width="16" height="10" rx="2" fill="#6ec44e" />
            <rect x="0" y="8" width="20" height="8" rx="2" fill="#5ab43e" />
            <rect x="4" y="6" width="3" height="3" fill="#2a2a2a" />
            <rect x="12" y="6" width="3" height="3" fill="#2a2a2a" />
            <rect x="5" y="7" width="1" height="1" fill="#ffffff" />
            <rect x="13" y="7" width="1" height="1" fill="#ffffff" />
          </g>
        ) : (
          <g key={slime.id} transform={`translate(${slime.x}, ${104})`} opacity="0.3">
            <rect x="2" y="0" width="16" height="4" rx="1" fill="#5ab43e" />
            {/* Sparkle */}
            <rect x="8" y="-8" width="2" height="2" fill="#ffd700" opacity={(frame % 4 < 2) ? 1 : 0} />
            <rect x="14" y="-4" width="2" height="2" fill="#ffd700" opacity={(frame % 4 >= 2) ? 1 : 0} />
          </g>
        )
      )}

      {/* Floating damage numbers */}
      {slimes.map((slime) =>
        slime.hit && slime.alive ? (
          <text
            key={`dmg-${slime.id}`}
            x={slime.x + 8}
            y={82}
            textAnchor="middle"
            fill="#e85d4a"
            fontSize="8"
            fontFamily="monospace"
            fontWeight="bold"
          >
            -1
          </text>
        ) : null
      )}
    </svg>
  );
}
